import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  constructor() {
    /**
     * The cache of section HTML
     * @type {Map<string, string>}
     */
    this.cache = new Map();

    /**
     * The abort controllers by section ID
     * @type {Map<string, AbortController>}
     */
    this.abortControllersBySectionId = new Map();

    /**
     * The pending promises
     * @type {Map<string, Promise<string>>}
     */
    this.pendingPromises = new Map();

    window.addEventListener('load', this.cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options = {}) {
    const { cache = !Shopify.designMode, mode = 'full', url } = options;

    this.abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.abortControllersBySectionId.delete(sectionId);

      morphSection(sectionId, sectionHTML, mode);
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  abortPendingMorph(sectionId) {
    const existingAbortController =
      this.abortControllersBySectionId.get(sectionId);

    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(
    sectionId,
    useCache = true,
    url = new URL(window.location.href)
  ) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    // Return cached version
    if (useCache && this.cache.has(sectionUrl)) {
      return this.cache.get(sectionUrl);
    }

    // Return pending promise if exists
    if (this.pendingPromises.has(sectionUrl)) {
      return this.pendingPromises.get(sectionUrl);
    }

  const pendingPromise = fetch(sectionUrl)
  .then((response) => response.text())
  .then((sectionHTML) => {
    this.cache.set(sectionUrl, sectionHTML);

    return sectionHTML;
  });

this.pendingPromises.set(sectionUrl, pendingPromise);

const sectionHTML = await pendingPromise;

this.pendingPromises.delete(sectionUrl);

return sectionHTML;
  }

  /**
   * Caches the page sections
   */
  cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);

      if (this.cache.get(url)) continue;
      if (containsShadowRoot(section)) continue;

      this.cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(
  sectionId,
  url = new URL(window.location.href)
) {
  const newUrl = new URL(url);

  newUrl.searchParams.set(
    'section_id',
    normalizeSectionId(sectionId)
  );

  newUrl.searchParams.sort();

  return newUrl.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(
    new RegExp(`^${SECTION_ID_PREFIX}`),
    ''
  );
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return (
    !!element.shadowRoot ||
    Array.from(element.children).some(containsShadowRoot)
  );
}

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 * @param {'hydration'|'full'} [mode]
 * @param {Object} [options]
 * @param {boolean} [options.injectStylesheet=false]
 */
export async function morphSection(
  sectionId,
  html,
  mode = 'full',
  options = {}
) {
  const { injectStylesheet = false } = options;

  const fragment = new DOMParser().parseFromString(
    html,
    'text/html'
  );

  const existingElement = document.getElementById(
    buildSectionSelector(sectionId)
  );

  const newElement = fragment.getElementById(
    buildSectionSelector(sectionId)
  );

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(
      `Section ${sectionId} not found in the section rendering response`
    );
  }

  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    childrenOnly: true,
    hydrationMode: mode === 'hydration',
  });

  if (injectStylesheet) {
    injectSectionStylesheet(fragment, existingElement);
  }
}

/**
 * Injects stylesheet into section
 */
function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector(
    'style[data-section-stylesheet]'
  );

  if (!newStylesheet) return;

  const existingStylesheet = sectionElement.querySelector(
    'style[data-section-stylesheet]'
  );

  if (existingStylesheet) {
    existingStylesheet.textContent =
      newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}

export const sectionRenderer = new SectionRenderer();